import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Col, Form, InputNumber, Row, Select, Spin } from 'antd';
import debounce from 'lodash/debounce';
import { whenOrderIsCreated, whenOrderIsFailed } from './messages';
import { searchForLocations } from '../../api/locations';
import styles from './CreateDeliveryOtpForm.module.less';
import { getErrorFromResponse, NetworkError } from '../../utils/apiHelpers';
import { Button } from '../../components/Button/Button';

export class CreateDeliveryOtpForm extends Component {
  state = {
    isLoading: false,
    showDropOff: true
  };
  constructor(props) {
    super(props);

    this.form = React.createRef();
    this.onDropOffSearch = debounce(this.onDropOffSearch, 300);
  }

  form;

  onFinish = async values => {
    const { onSubmit } = this.props;
    this.setState({
      isLoading: true
    });
    try {
      await onSubmit(values);
      await this._postOtpCreation();
    } catch (err) {
      this._postOtpCreation(err);
    } finally {
      this.setState({
        isLoading: false
      });
    }
  };

  resetFields = () => {
    this.form?.current.resetFields();
    this.setState({
      showDropOff: true
    });
  };

  _postOtpCreation = error => {
    if (!error) {
      this.resetFields();
      whenOrderIsCreated();
    } else if (error instanceof NetworkError) {
      this.resetFields();
    } else {
      whenOrderIsFailed(getErrorFromResponse(error));
    }
  };

  onPickupLocationChange = () => {
    const { resetFields } = this.form.current;

    resetFields(['dropOffLocation']);

    this.props.onPickupLocationChange();
  };

  onDropOffSearch = query => {
    if (!query) {
      return;
    }

    const pickupLocationId = this.form.current.getFieldValue('pickupLocation');
    this.props.onDropOffSearch(pickupLocationId, query);
  };

  componentDidUpdate() {
    //@ts-ignore
    if (!this?.form?.current.getFieldValue('pickupLocation')) {
      this.props.initialPickupLocationId &&
        this?.form?.current?.setFieldValue('pickupLocation', this.props.initialPickupLocationId);
    }
  }

  render() {
    const { isLoading, showDropOff } = this.state;
    const { availablePickupLocations, availableDropOffLocations, initialPickupLocationId, dropOffSearchLoading, t } =
      this.props;

    return (
      <div className={styles.container}>
        <Form
          ref={this.form}
          layout="vertical"
          hideRequiredMark
          onFinish={this.onFinish}
          data-testid="CreateDeliveryOtpForm"
          className={styles.form}
        >
          <div className={styles.formContent}>
            <div>
              <Row gutter={24}>
                <Col xs={24}>
                  <Form.Item
                    name="pickupLocation"
                    label={t('deliveries.pickupLocation.label')}
                    rules={[{ required: true, message: t('deliveries.pickupLocation.missing') }]}
                    initialValue={initialPickupLocationId}
                  >
                    <Select
                      showSearch
                      placeholder={t('deliveries.pickupLocation.placeholder')}
                      optionFilterProp="children"
                      filterOption={true}
                      onChange={this.onPickupLocationChange}
                      data-testid="pickupLocation"
                    >
                      {availablePickupLocations.map(location => (
                        <Select.Option
                          value={location.id}
                          key={`${location.id}`}
                          className={styles.option}
                          data-testid={`pickupLocationItem`}
                        >
                          {`${location.nickname} - ${location.building} - ${location.street} - ${location.area} - ${location.city}`}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col xs={24}>
                  <Form.Item
                    name="numberOfOtps"
                    label={t('deliveries.numberOfOtps.label')}
                    rules={[{ required: true, message: t('deliveries.numberOfOtps.missing') }]}
                    initialValue={1}
                  >
                    <InputNumber
                      min={1}
                      max={20}
                      className={styles.inputNumber}
                      data-testid="numberOfOtps"
                      parser={value => parseInt(value) || ''}
                      onChange={value => {
                        this.setState({
                          showDropOff: value <= 1
                        });
                      }}
                      type="number"
                    />
                  </Form.Item>
                </Col>
              </Row>
              {showDropOff ? (
                <Row gutter={24}>
                  <Col xs={24}>
                    <Form.Item
                      name="dropOffLocation"
                      label={t('deliveries.dropoffLocation.optionalLabel')}
                      rules={[{ required: false }]}
                    >
                      <Select
                        allowClear
                        showSearch
                        defaultActiveFirstOption={false}
                        placeholder={t('deliveries.dropoffLocation.placeholder')}
                        notFoundContent={dropOffSearchLoading ? <Spin /> : t('deliveries.dropoffLocation.missing')}
                        optionFilterProp="children"
                        filterOption={false}
                        onSearch={this.onDropOffSearch}
                        data-testid="dropOffLocation"
                      >
                        {availableDropOffLocations.map(location => (
                          <Select.Option
                            value={location.id}
                            key={`${location.id}`}
                            className={styles.option}
                            data-testid="dropOffLocationItem"
                          >
                            {location.formattedAddress}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              ) : (
                ''
              )}
            </div>
            <div className={styles.submitButtonContainer}>
              <div className={styles.submitButtonSize}>
                <Button type="primary" htmlType="submit" block size="large" disabled={isLoading}>
                  {t('deliveryOtps.createOTPButtonText')}
                </Button>
              </div>
            </div>
          </div>
        </Form>
      </div>
    );
  }
}

CreateDeliveryOtpForm.propTypes = {
  availablePickupLocations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string
    })
  ).isRequired,
  availableDropOffLocations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string
    })
  ).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onPickupLocationChange: PropTypes.func.isRequired,
  onDropOffSearch: PropTypes.func.isRequired,
  dropOffSearchLoading: PropTypes.bool,
  initialPickupLocationId: PropTypes.number,
  form: PropTypes.any,
  t: PropTypes.func,
  defaultPaymentMethodId: PropTypes.number
};

export const EnhancedCreateDeliveryOtpForm = withTranslation()(CreateDeliveryOtpForm);

export class CreateDeliveryOtpFormContainer extends Component {
  constructor(props) {
    super(props);

    this.state = {
      availableDropOffLocations: [],
      dropOffSearchLoading: false
    };
  }

  componentDidMount() {
    this.props.fetchPickupLocations();
  }

  onSubmit = ({ pickupLocation: pickupLocationId, dropOffLocation: dropOffLocationId, numberOfOtps }) => {
    const { defaultPaymentMethodId, cities, isWalletCreditUsed, secondaryPaymentMethodId } = this.props;
    const pickupLocation = this._getPickupLocationById(pickupLocationId);
    const { id: locationCityId } = (cities && cities.find(city => city.name === pickupLocation.city)) || { id: null };

    const pickupLocationWithCityId = pickupLocation
      ? {
          city_id: locationCityId,
          ...pickupLocation
        }
      : null;
    const dropOffLocation = this._getDropOffLocationById(dropOffLocationId);

    const paymentMethodId = isWalletCreditUsed ? secondaryPaymentMethodId : defaultPaymentMethodId;

    const otpRequest = {
      pickupLocation: pickupLocationWithCityId,
      dropOffLocation,
      numberOfOtps,
      defaultPaymentMethodId: paymentMethodId
    };

    return this.props.createOtp(otpRequest);
  };

  onPickupLocationChange = () => {
    this.setState({
      availableDropOffLocations: []
    });
  };

  _getPickupLocationById = id => {
    return this.props.pickupLocations.find(location => location.id === id);
  };

  _getDropOffLocationById = id => {
    return this.state.availableDropOffLocations.find(location => location.id === id);
  };

  onDropOffSearch = (pickupLocationId, query) => {
    const location = this._getPickupLocationById(pickupLocationId);

    if (!location) {
      return;
    }

    this.setState({
      dropOffSearchLoading: true
    });

    const latLng = location.location;

    searchForLocations(query, latLng).then(availableDropOffLocations => {
      if (availableDropOffLocations) {
        this.setState({
          availableDropOffLocations,
          dropOffSearchLoading: false
        });
      }
    });
  };

  render() {
    const { availableDropOffLocations, dropOffSearchLoading } = this.state;
    const { pickupLocations, defaultPickupLocation } = this.props;

    return (
      <EnhancedCreateDeliveryOtpForm
        onSubmit={this.onSubmit}
        onPickupLocationChange={this.onPickupLocationChange}
        onDropOffSearch={this.onDropOffSearch}
        dropOffSearchLoading={dropOffSearchLoading}
        availablePickupLocations={pickupLocations}
        availableDropOffLocations={availableDropOffLocations}
        initialPickupLocationId={defaultPickupLocation && defaultPickupLocation.id}
      />
    );
  }
}

CreateDeliveryOtpFormContainer.propTypes = {
  createOtp: PropTypes.func,
  fetchPickupLocations: PropTypes.func,
  selectPickupLocation: PropTypes.func,
  defaultPickupLocation: PropTypes.object,
  pickupLocations: PropTypes.arrayOf(PropTypes.any),
  defaultPaymentMethodId: PropTypes.number,
  cities: PropTypes.arrayOf(PropTypes.any),
  isWalletCreditUsed: PropTypes.bool,
  secondaryPaymentMethodId: PropTypes.number
};

const mapDispatch = ({ deliveryOtps: { createOtp }, user: { fetchPickupLocations, selectPickupLocation } }) => ({
  createOtp,
  fetchPickupLocations,
  selectPickupLocation
});

const mapState = ({
  user: {
    pickupLocations,
    defaultPickupLocation,
    defaultPaymentMethodId,
    cities,
    isWalletCreditUsed,
    secondaryPaymentMethodId
  }
}) => ({
  defaultPickupLocation,
  pickupLocations,
  defaultPaymentMethodId,
  cities,
  isWalletCreditUsed,
  secondaryPaymentMethodId
});

export default connect(mapState, mapDispatch)(CreateDeliveryOtpFormContainer);
